import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact Picker API — available in Chrome on Android, not on iPhone or desktop.
const isSupported = () =>
  typeof navigator !== 'undefined' && 'contacts' in navigator && typeof window !== 'undefined' && 'ContactsManager' in window;

const formatAddress = (a) => {
  if (!a) return null;
  const parts = [...(a.addressLine || []), a.city, a.region, a.postalCode, a.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

const toPayload = (c) => ({
  name: (c.name || [])[0] || (c.email || [])[0] || (c.tel || [])[0] || '',
  email: (c.email || [])[0] || null,
  phone: (c.tel || [])[0] || null,
  mobile: (c.tel || [])[1] || null,
  address: formatAddress((c.address || [])[0]),
});

export const PickFromPhoneButton = ({ onDone, className = '' }) => {
  const [busy, setBusy] = useState(false);
  if (!isSupported()) return null;

  const handlePick = async () => {
    try {
      const available = await navigator.contacts.getProperties();
      const props = ['name', 'email', 'tel', 'address'].filter((p) => available.includes(p));
      const picked = await navigator.contacts.select(props, { multiple: true });
      const contacts = (picked || []).map(toPayload).filter((c) => c.name.trim());
      if (!contacts.length) return;
      setBusy(true);
      const { data } = await axios.post(
        `${API}/contacts/bulk-create`,
        { contacts },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const skipped = data.skipped_duplicates || 0;
      toast.success(`Added ${data.imported_count} contact(s)${skipped ? ` · ${skipped} already existed` : ''}`);
      await onDone?.();
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'InvalidStateError') return;
      toast.error(err?.response?.data?.detail || 'Could not add contacts from your phone.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handlePick}
      disabled={busy}
      data-testid="contact-pick-from-phone-button"
      className={`flex items-center justify-center gap-2 bg-white border border-emerald-200 text-emerald-700 rounded-xl py-3 font-medium hover:bg-emerald-50 transition-colors disabled:opacity-60 ${className}`}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      {busy ? 'Adding…' : 'Pick from phone'}
    </button>
  );
};
